<script lang="ts">
  import { Sheet, SheetContent } from '@gat-ship/vue-components/sheet'
  import {
    MultiStepFormProvider,
    MultiStepFormMenu,
    MultiStepFormMenuItem,
    MultiStepFormContent,
    MultiStepFormHeader,
    MultiStepFormButtonLast,
    MultiStepFormButtonNext,
    type FormStep
  } from '../multi-step-form'
  import type { IconValues } from '@gat-ship/icons'
  import { createContext } from 'reka-ui'
  import { type Ref, ref } from 'vue'

  export interface WizardContext {
    provided: boolean
    isOpen: Ref<boolean | undefined>
  }

  const [_useWizard, provideWizard] = createContext<WizardContext>('gs-wizard')

  /** Used to check the type of the modal. Useful in for determining subcomponent's styles. */
  export function useWizardContext() {
    try {
      return _useWizard()
    } catch (error) {
      return {
        provided: false,
        isOpen: ref(false)
      }
    }
  }

  export interface WizardStep<T extends string> extends FormStep {
    id: T
    title: string
    description: string
    icon: IconValues
  }

  export interface WizardProps<T extends string> {
    steps: WizardStep<T>[]
    menuTitle: string
  }
</script>

<script setup lang="ts" generic="T extends string">
  import { useRouter } from '@gat-ship/lib/router'
  import { watch } from 'vue'

  const props = defineProps<WizardProps<T>>()

  const isOpen = defineModel<boolean>('open')

  const router = useRouter()

  watch(isOpen, (newIsOpen) => {
    router.forceStopNavigation.value = newIsOpen ?? false
  })
  provideWizard({
    provided: true,
    isOpen
  })

  defineExpose({
    isOpen
  })
</script>

<template>
  <sheet v-model:open="isOpen">
    <sheet-content
      side="bottom"
      class="flex h-screen w-screen px-0.5 py-1"
      @click:close="isOpen = false"
      @escape-key-down="isOpen = false"
    >
      <multi-step-form-provider :steps="steps">
        <multi-step-form-menu :title="menuTitle" class="h-full px-4 pb-4 pt-5">
          <multi-step-form-menu-item
            v-for="(step, index) in steps"
            :key="step.id"
            :value="index"
          >
            {{ step.title }}
          </multi-step-form-menu-item>
        </multi-step-form-menu>
        <div
          class="flex h-full w-full flex-col items-center overflow-y-auto py-12"
        >
          <multi-step-form-content
            v-for="(step, index) in steps"
            :key="step.id"
            :value="index"
          >
            <div class="flex h-fit w-full grow flex-col gap-6">
              <slot :name="`${step.id}-header`" :step="step">
                <multi-step-form-header class="max-w-[500px] self-center" />
              </slot>
              <div class="flex w-full max-w-[480px] flex-col self-center px-12">
                <slot name="before-content" :step="step" />
                <div
                  class="shadow-xs w-full rounded-sm ring-1 ring-inset ring-gray-200 dark:ring-slate-700"
                >
                  <slot name="content-before" :step="step" />
                  <slot :name="`${step.id}-content`" :step="step" />
                  <slot name="content-after" :step="step" />
                </div>
                <slot name="after-content" :step="step" />
              </div>
            </div>
          </multi-step-form-content>
        </div>
      </multi-step-form-provider>
    </sheet-content>
  </sheet>
</template>
